import { useEffect, useState } from 'react';
import { Modal, Tag, Spin, Alert } from 'antd';
import { resolvePricesSafe, itemRefKey } from '../../masters/priceLists/priceListApi';

// ════════════════════════════════════════════════════════════════════
// Stage 6PL-1e — "Why this rate?"
//
// THE ONE THING THIS SCREEN MUST NOT DO IS GUESS.
//
// There are two different questions hiding behind the same words, and answering
// the wrong one is how a support tool becomes a liar:
//
//   1. "Where did this line's rate COME FROM?"  — answered ONLY by the stamp
//      frozen on the line at save (migration 0182). It is history and it cannot
//      be recomputed, because the card may have been revised since.
//
//   2. "What WOULD this line cost today?"       — answered by re-resolving.
//
// So this panel shows both, separately labelled, and says so out loud when they
// disagree. A panel that quietly re-resolved and presented the answer as
// history would confidently show a number that was never charged — which is
// exactly the failure migration 0182 exists to prevent.
//
// A line with no stamp (priced before the feature, or a free-typed line) says
// "not recorded" rather than inventing MASTER.
// ════════════════════════════════════════════════════════════════════

const SOURCE_TAG = {
  CUSTOMER_LIST: { color: 'blue', label: 'Customer price list' },
  GROUP_LIST: { color: 'geekblue', label: 'Price-group list' },
  MASTER: { color: 'default', label: 'Item master' },
  MANUAL: { color: 'orange', label: 'Typed by hand' }
};
const SCOPE_LABEL = { CUSTOMER_LIST: 'this customer', GROUP_LIST: 'price group' };

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function PriceRationaleModal({ open, onClose, line, priceList }) {
  const [loading, setLoading] = useState(false);
  const [today, setToday] = useState(null);   // { lists, rates } — what WOULD apply now

  const customerId = (priceList && priceList.customerId) || null;
  const asOfDate = (priceList && priceList.asOfDate) || null;
  const itemId = (line && line.itemId) || null;

  useEffect(() => {
    if (!open || !itemId || !customerId) { setToday(null); return undefined; }
    let alive = true;
    setLoading(true);
    resolvePricesSafe({ customerId, asOfDate, refs: [{ kind: 'ITEM', refId: itemId }] })
      .then((r) => { if (alive) setToday(r); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, itemId, customerId, asOfDate]);

  if (!line) return null;

  const stamp = line.priceSource || null;
  const stampTag = stamp ? (SOURCE_TAG[stamp] || { color: 'default', label: stamp }) : null;
  const masterRate = line.itemSnapshot && line.itemSnapshot.salesRate != null
    ? Number(line.itemSnapshot.salesRate) : null;

  const lists = (today && Array.isArray(today.lists)) ? today.lists : [];
  const liveHit = (today && itemId) ? today.rates[itemRefKey(itemId)] : null;
  const liveRate = liveHit ? Number(liveHit.rate) : masterRate;
  const lineRate = Number(line.salesRate);
  // Only meaningful once we actually know what today would say.
  const drifted = today && Number.isFinite(liveRate) && Number.isFinite(lineRate)
    && Math.abs(liveRate - lineRate) > 0.005 && stamp !== 'MANUAL';

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={560}
      title="Why this rate?" destroyOnHidden>
      <div style={{ fontSize: 13 }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>Line</div>
          <div style={{ fontWeight: 600 }}>{line.itemDesc || '—'}</div>
        </div>

        {/* ── 1. HISTORY: the stamp. Never recomputed. ── */}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              This line is priced at
            </span>
            <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              ₹{money(lineRate)}
              <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}> / {line.priceUomCode || '—'}</span>
            </span>
          </div>
          <div style={{ marginTop: 6 }}>
            {stampTag
              ? <Tag color={stampTag.color}>{stampTag.label}</Tag>
              : <Tag>Source not recorded</Tag>}
            {line.priceListId && stamp !== 'MANUAL'
              ? <span style={{ fontSize: 11, color: '#64748b' }}>
                  from the card stamped on this line when it was saved
                </span>
              : null}
            {!stamp
              ? <span style={{ fontSize: 11, color: '#64748b' }}>
                  priced before price lists existed, or typed without picking an item
                </span>
              : null}
          </div>
        </div>

        {/* ── 2. TODAY: a fresh resolution, labelled as such. ── */}
        <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
          What applies today{asOfDate ? ` (as at ${asOfDate})` : ''}
        </div>

        {!customerId ? (
          <Alert type="info" showIcon style={{ fontSize: 12 }}
            message="No customer is selected, so no price list can apply. This line is on master rates." />
        ) : !itemId ? (
          <Alert type="info" showIcon style={{ fontSize: 12 }}
            message="This line has no item from the Item Master, so there is nothing for a price list to override. The rate was typed." />
        ) : loading ? (
          <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>
        ) : (
          <>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              {lists.length === 0 ? (
                <div style={{ padding: '8px 12px', color: '#64748b' }}>
                  No price list reaches this customer on this date.
                </div>
              ) : lists.map((l) => {
                const won = liveHit && liveHit.priceListId === l.id;
                return (
                  <div key={l.id} style={{
                    padding: '8px 12px', borderBottom: '1px solid #f1f5f9',
                    background: won ? '#eff6ff' : undefined
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span>
                        <strong>{l.listCode}</strong>
                        <span style={{ color: '#64748b' }}> · {l.listName}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}> · via {SCOPE_LABEL[l.source] || l.source}</span>
                      </span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {won
                          ? <strong>₹{money(liveHit.rate)}</strong>
                          : <span style={{ color: '#94a3b8' }}>no rate for this item</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div style={{
                padding: '8px 12px',
                background: !liveHit ? '#eff6ff' : undefined
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span><strong>Item master</strong><span style={{ color: '#64748b' }}> · the default</span></span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {masterRate != null
                      ? (!liveHit ? <strong>₹{money(masterRate)}</strong> : `₹${money(masterRate)}`)
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
              The first card carrying a rate for this item wins. A card with no row for it is skipped,
              so a customer list can override some items and inherit the rest.
            </div>
          </>
        )}

        {/* ── 3. The honest part: say it when the two disagree. ── */}
        {drifted ? (
          <Alert type="warning" showIcon style={{ marginTop: 12, fontSize: 12 }}
            message="This line does not match what would apply today"
            description={`The line is saved at ₹${money(lineRate)}; resolving now gives ₹${money(liveRate)}. The line keeps what it was priced at — a price list never re-prices a saved document. To use the newer rate, re-pick the item on this line.`} />
        ) : null}
        {stamp === 'MANUAL' ? (
          <Alert type="info" showIcon style={{ marginTop: 12, fontSize: 12 }}
            message="This rate was typed by hand"
            description="A hand-typed rate is never replaced automatically — not by a price list and not by changing the customer." />
        ) : null}
      </div>
    </Modal>
  );
}
